/**
 * Feature Flags React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureFlagsApi } from '@/lib/api/featureFlags';
import type { FeatureFlag, UserOverrideRequest } from '@/types/featureFlags';

const QUERY_KEY = ['feature-flags'];

export function useFeatureFlags() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: featureFlagsApi.getConfig,
  });
}

export function useUserOverrides() {
  return useQuery({
    queryKey: [...QUERY_KEY, 'user-overrides'],
    queryFn: featureFlagsApi.getUserOverrides,
  });
}

export function useAuditLogs(limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'audit', limit, offset],
    queryFn: () => featureFlagsApi.getAuditLogs(limit, offset),
  });
}

export function useUpdateFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      flags,
      changeReason,
    }: {
      flags: Record<string, FeatureFlag>;
      changeReason?: string;
    }) => featureFlagsApi.updateConfig(flags, changeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useToggleFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      flagName,
      enabled,
      changeReason,
      broadcastUpdate,
    }: {
      flagName: string;
      enabled: boolean;
      changeReason?: string;
      broadcastUpdate?: boolean;
    }) => featureFlagsApi.toggleFlag(flagName, enabled, changeReason, broadcastUpdate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useSetUserOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserOverrideRequest) =>
      featureFlagsApi.setUserOverride(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteUserOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      flagName,
      changeReason,
    }: {
      userId: string;
      flagName: string;
      changeReason?: string;
    }) => featureFlagsApi.deleteUserOverride(userId, flagName, changeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteAllUserOverrides() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      changeReason,
    }: {
      userId: string;
      changeReason?: string;
    }) => featureFlagsApi.deleteAllUserOverrides(userId, changeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
